import { toast } from '@/components/toast';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import { useTiming } from '@/hooks/useTiming';
import {
    getTotalVideoDuration,
    useGenerationMixpanelData,
} from '@/pages/AdvancedEditor/components/header/Generate/GenerateButton/useGenerationMixpanelData';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useCurrentProjectId } from '@/pages/AdvancedEditor/hooks/useCurrentProjectId';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { ERROR_KINDS, moderationError } from '@/state/layout/moderation';
import { AppRoutes, MixPanelEvent } from '@/utils/types';
import { HttpStatus } from '@d-id/http-status';
import mixpanel from 'mixpanel-browser';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import useGenerateScenes from '../../../../hooks/useGenerateScenes';
import { GenerateButton } from './GenerateButton';

interface ProjectRejectedFriendlyToast {
    title: string;
    message: string;
    type?: 'error' | 'info';
}

export const GenerateButtonWrapper = observer(() => {
    const navigate = useNavigate();
    const { store, saveArtifacts, isGenerating } = useAdvancedEditorContext();
    const { withTiming } = useTiming();

    const setModerationError = useSetRecoilState(moderationError);
    const projectId = useCurrentProjectId();
    const generateScenes = useGenerateScenes(projectId);
    const videoDuration = getTotalVideoDuration(store);
    const projectRejectedErrorToFriendlyToastJson = useFlag<Record<string, ProjectRejectedFriendlyToast>>(
        'project_rejected_error_to_friendly_toast_json',
        {}
    );
    const mixpanelData = useGenerationMixpanelData(store);
    const { incrementUsage } = useFreeVideos();

    const [, setOpen] = useState(false);

    const handleClose = () => {
        if (!isGenerating.current && videoDuration) setOpen(false);
    };

    const handleGenerate = async (onSuccess?: () => void) => {
        if (isGenerating.current) {
            return;
        }

        await withTiming('handleGenerate', async () => {
            try {
                mixpanel?.track('generate', {
                    ...mixpanelData,
                    event: MixPanelEvent.CLICK,
                });

                await withTiming('saveArtifacts', () => saveArtifacts(store, false));

                isGenerating.current = true;

                await withTiming('generateScenes', () => generateScenes());

                mixpanel?.track('generate', {
                    ...mixpanelData,
                    event: MixPanelEvent.SUCCESS,
                });
                await withTiming('incrementUsage', () => incrementUsage.mutateAsync(1));
                onSuccess?.();

                setTimeout(() => {
                    navigate(AppRoutes.VideoLibrary, {
                        state: { resetState: true, isAfterGenerate: true },
                    });
                });
            } catch (error: any) {
                let kind = error?.response?.data?.kind;
                const terms = [...new Set(error?.response?.data?.details?.terms)];

                if (kind === ERROR_KINDS.TextModerationError) {
                    setModerationError({
                        kind: ERROR_KINDS.TextModerationError,
                        extraData: terms,
                    });
                } else if (kind === ERROR_KINDS.AudioModerationError) {
                    setModerationError({
                        kind: ERROR_KINDS.AudioModerationError,
                        extraData: terms,
                    });
                } else {
                    if (error?.response?.status === HttpStatus.TOO_MANY_REQUESTS) {
                        kind = 'TooManyRequestsError';
                    }

                    logger.error('Could not generate project', { error });
                }
                const internalErrorReason =
                    kind ||
                    error?.response?.data?.description ||
                    error?.response?.data?.message ||
                    error?.message ||
                    'Unknown internal error reject message';

                const { title, message, type } = projectRejectedErrorToFriendlyToastJson[internalErrorReason] ||
                    projectRejectedErrorToFriendlyToastJson['DefaultError'] || {
                        title: 'Failed to generate your video',
                        message: 'Please try again',
                        type: 'error',
                    };

                if (title) {
                    const toastMethod = type === 'info' ? toast.info : toast.error;
                    toastMethod({
                        title,
                        message,
                        mixpanelCustomProps: {
                            'toast-internal-reason': internalErrorReason,
                            'internal-failure-type': 'rejected',
                        },
                    });
                }

                mixpanel?.track('generate', {
                    ...mixpanelData,
                    event: MixPanelEvent.ERROR,
                    'toast-internal-reason': internalErrorReason,
                });
                isGenerating.current = false;
                handleClose();
            }
        });
    };

    const onOpenGenerate = async () => {
        mixpanel?.track('generate', {
            ...mixpanelData,
            event: MixPanelEvent.VIEW,
            projectId,
        });
        setOpen(true);
    };

    return <GenerateButton onCtaClick={handleGenerate} onOpen={onOpenGenerate} isProcessing={isGenerating.current} />;
});
